import type {
  PatientResponse,
  PatientUpdate,
  EmploymentStatus,
  Gender,
  MaritalStatus,
} from '../../services/api/types.gen';
import { parseISOToLocalDate } from '../../utils/dateUtils';
import {
  formatSSN,
  formatStoredPhone,
  isCompleteUsPhone,
} from '../../utils/intake';

// Shared by PatientInfoTab (autosave) and its section components (field
// grids). Kept out of the tab so a section can be imported without pulling in
// the API client and the autosave machinery.

export type PatientInfoFormState = {
  first_name: string;
  last_name: string;
  middle_name: string;
  suffix: string;
  date_of_birth: Date | undefined;
  gender: Gender | '';
  marital_status: MaritalStatus | '';
  employment_status: EmploymentStatus | '';
  primary_language: string;
  ssn: string;
  mbi: string;
  mrn: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  county: string;
  emergency_contact: string;
  emergency_contact_relationship: string;
  emergency_phone: string;
  secondary_contact: string;
  secondary_contact_relationship: string;
  secondary_phone: string;
  needs_legal_representative: boolean;
  has_poa: boolean;
};

export type UpdatePatientInfoField = <K extends keyof PatientInfoFormState>(
  key: K,
  value: PatientInfoFormState[K]
) => void;

// The props every field-grid section takes. Sections are controlled by the
// tab, which owns the form state and the autosave that watches it.
export interface PatientInfoSectionProps {
  form: PatientInfoFormState;
  updateField: UpdatePatientInfoField;
  canEditPatientInfo: boolean;
}

export function formatPatientPhone(value: string | null | undefined): string {
  // Keep malformed legacy values visible so hydration cannot silently discard
  // data. Valid stored values are normalized to the edit-form format.
  return formatStoredPhone(value) || value || '';
}

export function patientToForm(patient: PatientResponse): PatientInfoFormState {
  return {
    first_name: patient.first_name ?? '',
    last_name: patient.last_name ?? '',
    middle_name: patient.middle_name ?? '',
    suffix: patient.suffix ?? '',
    date_of_birth: patient.date_of_birth
      ? (parseISOToLocalDate(patient.date_of_birth) ?? undefined)
      : undefined,
    gender: (patient.gender as Gender) ?? '',
    marital_status: patient.marital_status ?? '',
    employment_status: patient.employment_status ?? '',
    primary_language: patient.primary_language ?? '',
    ssn: patient.ssn ? formatSSN(patient.ssn) : '',
    mbi: patient.mbi ?? '',
    mrn: patient.mrn ?? '',
    phone: formatPatientPhone(patient.phone),
    email: patient.email ?? '',
    address: patient.address ?? '',
    city: patient.city ?? '',
    state: patient.state ?? '',
    zip_code: patient.zip_code ?? '',
    county: patient.county ?? '',
    emergency_contact: patient.emergency_contact ?? '',
    emergency_contact_relationship:
      patient.emergency_contact_relationship ?? '',
    emergency_phone: formatPatientPhone(patient.emergency_phone),
    secondary_contact: patient.secondary_contact ?? '',
    secondary_contact_relationship:
      patient.secondary_contact_relationship ?? '',
    secondary_phone: formatPatientPhone(patient.secondary_phone),
    needs_legal_representative: patient.needs_legal_representative ?? false,
    has_poa: patient.has_poa ?? false,
  };
}

export function formsDiffer(
  a: PatientInfoFormState,
  b: PatientInfoFormState
): boolean {
  return (Object.keys(a) as (keyof PatientInfoFormState)[]).some(key => {
    const left = a[key];
    const right = b[key];
    if (left instanceof Date && right instanceof Date) {
      return left.getTime() !== right.getTime();
    }
    if (left instanceof Date || right instanceof Date) {
      return (left === undefined) !== (right === undefined);
    }
    return left !== right;
  });
}

// The patient phone and both contact phones share one validation rule and one
// error copy, so a change to the rule cannot drift between sections.
export const PHONE_FIELDS = [
  'phone',
  'emergency_phone',
  'secondary_phone',
] as const;

export type PhoneField = (typeof PHONE_FIELDS)[number];
export const PHONE_ERROR_MESSAGE = 'Enter a 10-digit US phone number';

export function isPhoneField(
  key: keyof PatientInfoFormState
): key is PhoneField {
  return (PHONE_FIELDS as readonly string[]).includes(key);
}

// Same rule the API enforces, so a bad MRN is refused at the keystroke rather
// than by a 422 after the debounce. Empty passes: an agency may hold a chart
// with no MRN, and clearing the field is a real edit.
export const MRN_ERROR_MESSAGE =
  'MRN must contain only letters and numbers and cannot start with "test"';

export function isValidMrn(value: string): boolean {
  const mrn = value.trim();
  if (!mrn) return true;
  return /^[A-Za-z0-9]+$/.test(mrn) && !mrn.toLowerCase().startsWith('test');
}

// Autosave must not fire on a half-typed MRN, but it must also not stall on
// one the user has not touched: a legacy value that would fail the rule today
// is still allowed to sit there while they edit their address.
export function hasValidChangedMrn(
  form: PatientInfoFormState,
  lastSavedForm: PatientInfoFormState
): boolean {
  return form.mrn === lastSavedForm.mrn || isValidMrn(form.mrn);
}

// Send the MRN only when it changed. Imported charts carry MRNs the current
// rule would reject (hyphens, dots, a "test" prefix), and the API validates
// whatever it is given, so putting an untouched legacy value in every body
// would 422 the patient's whole form on any edit.
export function getChangedMrnUpdate(
  form: PatientInfoFormState,
  lastSavedForm: PatientInfoFormState,
  explicitlyChanged = false
): Pick<PatientUpdate, 'mrn'> {
  if (form.mrn === lastSavedForm.mrn && !explicitlyChanged) return {};
  return { mrn: form.mrn.trim() || null };
}

export function hasValidChangedPhones(
  form: PatientInfoFormState,
  lastSavedForm: PatientInfoFormState
): boolean {
  return PHONE_FIELDS.every(
    field =>
      form[field] === lastSavedForm[field] || isCompleteUsPhone(form[field])
  );
}

export function getChangedPhoneUpdates(
  form: PatientInfoFormState,
  lastSavedForm: PatientInfoFormState,
  explicitlyChangedFields: readonly PhoneField[] = []
): Pick<PatientUpdate, PhoneField> {
  const updates: Pick<PatientUpdate, PhoneField> = {};
  for (const field of PHONE_FIELDS) {
    if (
      form[field] !== lastSavedForm[field] ||
      explicitlyChangedFields.includes(field)
    ) {
      updates[field] = form[field].trim() || null;
    }
  }
  return updates;
}

// Radix rejects an empty SelectItem value, so an optional dropdown needs a
// sentinel to offer "no answer" back after something has been picked.
export const NONE_VALUE = '__none__';

// Column widths on the patients table, except for suffix, which the API caps at
// the narrower width the 837I name segments allow. The API enforces the same
// caps and answers 422, but stopping the keystroke is quicker feedback than a
// round trip.
export const MAX_LENGTHS = {
  mrn: 100,
  suffix: 10,
  county: 100,
  contactName: 255,
  contactRelationship: 100,
} as const;
